import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationDFx5 from './NavigationDFx5';
import Breadcrumb, { BreadcrumbItem } from './ui/Breadcrumb';
import ErrorBoundary from './ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const authPaths = ['/signin', '/signup', '/verify-email', '/forgot-password', '/reset-password'];

const pathLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/loes': 'Proposals',
  '/customers': 'Customers',
  '/countries': 'Countries',
  '/rates': 'Rates',
  '/roles': 'Roles',
  '/loe-types': 'LOE Types',
  '/admin': 'Admin Portal',
  '/sales-assets': 'Assets',
  '/profile': 'Profile',
};

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  if (pathname === '/') return [{ label: 'Dashboard' }];
  const crumbs: BreadcrumbItem[] = [{ label: 'Dashboard', href: '/' }];
  const segments = pathname.split('/').filter(Boolean);
  let path = '';
  for (const seg of segments) {
    path += `/${seg}`;
    const label = pathLabels[path] || seg.charAt(0).toUpperCase() + seg.slice(1);
    crumbs.push({ label, href: path });
  }
  // Last crumb has no link
  if (crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { label: crumbs[crumbs.length - 1].label };
  }
  return crumbs;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isAuthPage = authPaths.some(p => location.pathname.startsWith(p));
  const showSidebar = isAuthenticated && !isAuthPage;

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {showSidebar && (
        <ErrorBoundary>
          <NavigationDFx5 />
        </ErrorBoundary>
      )}

      <div className={showSidebar ? 'ml-[var(--sidebar-w)]' : ''}>
        {/* Topbar accent line */}
        {showSidebar && (
          <div className="h-0.5 bg-gradient-to-r from-[var(--primary-500)] via-[var(--coral-500)] to-[var(--primary-500)]" />
        )}

        {/* Topbar with breadcrumbs */}
        {showSidebar && (
          <header className="sticky top-0 z-30 bg-[var(--card)] border-b border-[var(--border)] px-6 py-3">
            <Breadcrumb items={buildBreadcrumbs(location.pathname)} />
          </header>
        )}

        {/* Content area — auth pages render full-bleed (they provide their own
            full-screen background); app pages get the padded, max-width shell. */}
        <main className={isAuthPage ? '' : 'px-6 py-6 max-w-[1400px] mx-auto'}>
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default Layout;
